import React, {FC, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {adaptive} from 'styles/mixins/Adaptive';
import {IStock} from 'modules/stock-x/types';
import {StockCard} from 'modules/stock-x/components/StockCard';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import {Mousewheel, Pagination} from 'swiper/modules';
import {Box} from 'components/Grid';
import {useSliderBannerViewTracking} from 'hooks/analytics/gtag/sliders/useSliderBannerViewTracking';
import {useBannerClickGtag} from 'hooks/analytics/gtag/banners/useBannerClickGtag';
import {SliderItemInView} from 'hooks/analytics/gtag/sliders/useSliderItemInView';
import {useGetStockListQuery} from 'modules/discount/services';
import {TPropsWithClassName} from 'core/types';
import classNames from 'classnames';
import {SkeletonWrapper} from 'modules/skeleton/components/SkeletonWrapper';

interface IProps {
    withTitle?: boolean;
    gtagEventsEnabled?: boolean;
    withoutCode?: IStock['code'];
}

const StocksSlider: FC<TPropsWithClassName<IProps>> = ({
    className,
    withTitle = true,
    gtagEventsEnabled = true,
    withoutCode,
}) => {
    const {data: stockList, isLoading} = useGetStockListQuery({
        withProducts: false,
        withoutCode,
    });

    const {handleItemView} = useSliderBannerViewTracking({
        list: (stockList as IStock[]) ?? [],
        isLoading,
        getId: (stock) => stock.id,
        getName: (stock) => stock.title,
        type: 'stock',
        creative: 'promo_page_another_promo',
        enabled: gtagEventsEnabled,
    });

    const bannerClickGtag = useBannerClickGtag();

    const breakpoints = {
        [adaptive.SIZE_TABLET_XL_MIN]: {
            spaceBetween: 16,
        },
        [adaptive.SIZE_TABLET_SM_MIN]: {
            spaceBetween: 12,
        },
        [adaptive.SIZE_SM_MIN]: {
            spaceBetween: 12,
        },
    };

    const [isInitialized, setIsInitialized] = useState(false);

    return (
        <SkeletonWrapper disabled={isInitialized} className={classNames(style.stocksSlider, className)}>
            <Box commonPaddingOnMobileTablet={true}>
                {withTitle && <Typography variant={'title-20-m'}>Другие акции</Typography>}
                <Swiper
                    className={style.slider}
                    slidesPerView={'auto'}
                    spaceBetween={16}
                    breakpoints={breakpoints}
                    mousewheel={{
                        enabled: true,
                        releaseOnEdges: true,
                    }}
                    modules={[Mousewheel, Pagination]}
                    pagination={{
                        clickable: true,
                    }}
                    onInit={() => {
                        setIsInitialized(true);
                    }}
                    grabCursor={true}
                    resistanceRatio={0}
                >
                    {((stockList as IStock[]) ?? [])?.map((stock, index) => {
                        const position = index + 1;

                        const handleClick = () => {
                            bannerClickGtag({
                                type: 'common',
                                creative: 'promo_page_another_promo',
                                banner: {
                                    id: stock.id,
                                    name: stock.title,
                                    position,
                                },
                            });
                        };

                        return (
                            <SwiperSlide
                                className={classNames(style.slide, {
                                    [style.isInitialized]: isInitialized,
                                })}
                                key={stock.id}
                            >
                                <SliderItemInView id={stock.id} index={position} onView={handleItemView}>
                                    <StockCard stock={stock} onClick={handleClick} />
                                </SliderItemInView>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </Box>
        </SkeletonWrapper>
    );
};

export default StocksSlider;
